<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import ReportScopeFilters from './ReportScopeFilters.vue';
import {
  generateReportURLParams,
  generateFilterURLParams,
  parseReportURLParams,
  parseFilterURLParams,
} from '../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  showScopeFilters: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const route = useRoute();
const router = useRouter();
const store = useStore();

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const businessHoursSelected = ref(false);
const agentId = ref(null);
const teamId = ref(null);
const inboxId = ref(null);

const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

const updateURLParams = () => {
  const params = generateReportURLParams({
    from: getUnixStartOfDay(customDateRange.value[0]),
    to: getUnixEndOfDay(customDateRange.value[1]),
    businessHours: businessHoursSelected.value,
    range: selectedDateRange.value,
  });

  const scopeParams = generateFilterURLParams({
    agent_id: agentId.value,
    team_id: teamId.value,
    inbox_id: inboxId.value,
  });

  router.replace({ query: { ...params, ...scopeParams } });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: getUnixStartOfDay(customDateRange.value[0]),
    to: getUnixEndOfDay(customDateRange.value[1]),
    businessHours: businessHoursSelected.value,
    agentId: agentId.value,
    teamId: teamId.value,
    inboxId: inboxId.value,
  });
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  emitChange();
};

const onBusinessHoursToggle = () => {
  emitChange();
};

const onScopeFilterChange = filters => {
  agentId.value = filters.agentId;
  teamId.value = filters.teamId;
  inboxId.value = filters.inboxId;
  emitChange();
};

const initializeFromURL = () => {
  const urlParams = parseReportURLParams(route.query);
  const scopeParams = parseFilterURLParams(route.query);

  // Set the range type first
  if (urlParams.range) {
    selectedDateRange.value = urlParams.range;
  }

  // Restore dates from URL if available
  if (urlParams.from && urlParams.to) {
    customDateRange.value = [
      new Date(urlParams.from * 1000),
      new Date(urlParams.to * 1000),
    ];
  }

  if (urlParams.businessHours) {
    businessHoursSelected.value = urlParams.businessHours;
  }

  agentId.value = scopeParams.agent_id;
  teamId.value = scopeParams.team_id;
  inboxId.value = scopeParams.inbox_id;
};

onMounted(async () => {
  initializeFromURL();
  if (props.showScopeFilters) {
    await Promise.all([
      store.dispatch('agents/get'),
      store.dispatch('teams/get'),
      store.dispatch('inboxes/get'),
    ]);
  }
  emitChange();
});
</script>

<template>
  <div
    class="flex flex-col justify-between gap-3 md:flex-row"
    :class="{ 'pointer-events-none opacity-50': disabled }"
  >
    <div class="flex flex-col flex-wrap items-start gap-2 md:flex-row">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <ReportScopeFilters
        v-if="showScopeFilters"
        :agent-id="agentId"
        :team-id="teamId"
        :inbox-id="inboxId"
        :agents="agents"
        :teams="teams"
        :inboxes="inboxes"
        :disabled="disabled"
        @filter-change="onScopeFilterChange"
      />
    </div>
    <div class="flex items-center">
      <span class="mx-2 text-sm whitespace-nowrap">
        {{ $t('REPORT.BUSINESS_HOURS') }}
      </span>
      <span>
        <ToggleSwitch
          v-model="businessHoursSelected"
          @change="onBusinessHoursToggle"
        />
      </span>
    </div>
  </div>
</template>
